/**
 * @file ProtectedRoute.jsx
 *
 * Generic route guard component. Redirects unauthenticated users to /login
 * and optionally restricts access to specific roles. Renders a spinner while
 * the auth state is still loading.
 */
import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { useProfessionalProfile } from "../hooks/useProfessionals";
import { Spinner } from "../ui";

/**
 * Route guard that requires authentication and optionally restricts by role.
 * For professionals, also enforces onboarding completion — redirects to /onboarding
 * if the professional hasn't finished the setup wizard.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - Child components to render when access is granted
 * @param {string[]} [props.roles] - Allowed roles (e.g., ["PROFESIONAL", "ADMIN"]). Omit to allow any authenticated user.
 * @returns {React.ReactElement}
 */
const ProtectedRoute = ({ children, roles }) => {
  const { user, isLoading, isAuthenticated } = useAuth();
  const location = useLocation();

  const isProfessional = isAuthenticated && user?.role === "PROFESIONAL";
  const isOnboardingPage = location.pathname === "/onboarding";
  const { profile, isLoading: profileLoading } = useProfessionalProfile(isProfessional && !isOnboardingPage);

  if (isLoading || (isProfessional && !isOnboardingPage && profileLoading)) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60dvh" }}>
        <Spinner size={32} />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (roles && !roles.includes(user.role)) {
    return <Navigate to="/" replace />;
  }

  // All users must accept terms before accessing any protected page
  if (!user.termsAccepted && location.pathname !== "/aceptar-terminos") {
    return <Navigate to="/aceptar-terminos" replace />;
  }

  // Professionals must complete onboarding before accessing any protected page
  // (only enforced after terms are accepted — otherwise we'd loop between the two gates)
  if (isProfessional && !isOnboardingPage && user.termsAccepted && profile && !profile.onboardingComplete) {
    return <Navigate to="/onboarding" replace />;
  }

  // Patients must verify their email before accessing the patient dashboard
  if (user.role === "PACIENTE" && !user.isEmailVerified && location.pathname !== "/verificar-email") {
    return <Navigate to="/verificar-email" replace />;
  }

  return children;
};

export default ProtectedRoute;
